import React from 'react';
import { Modal, Button, Space, Typography, message } from 'antd';
import { CalendarOutlined, FilePdfOutlined } from '@ant-design/icons';
import moment from 'moment';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const { Title, Text } = Typography;

const groupClassesByMonth = (classes) => {
  const grouped = {};
  classes.forEach((cls) => {
    const monthKey = moment(cls.date).format('YYYY-MM');
    if (!grouped[monthKey]) grouped[monthKey] = [];
    grouped[monthKey].push(cls);
  });
  return grouped;
};

const generateMonthCalendar = (yearMonth) => {
  const firstDay = moment(yearMonth, 'YYYY-MM').startOf('month');
  const lastDay = moment(yearMonth, 'YYYY-MM').endOf('month');
  const startDate = firstDay.clone().startOf('week');
  const endDate = lastDay.clone().endOf('week');

  const calendar = [];
  let week = [];
  const current = startDate.clone();

  while (current.isSameOrBefore(endDate)) {
    week.push(current.clone());
    if (week.length === 7) {
      calendar.push(week);
      week = [];
    }
    current.add(1, 'day');
  }
  return calendar;
};

const getClassesForDate = (date, monthClasses) =>
  monthClasses.filter(
    (cls) => moment(cls.date).format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
  );

// Resolve a single representative attendance status for a class.
// Priority: To be rescheduled > Absent > Late > Present > none.
const resolveClassStatus = (records) => {
  if (!records || records.length === 0) return null;
  if (records.some((r) => r.status === 'To be rescheduled')) return 'To be rescheduled';
  if (records.some((r) => r.status === 'Absent')) return 'Absent';
  if (records.some((r) => r.status === 'Late')) return 'Late';
  if (records.some((r) => r.status === 'Present')) return 'Present';
  return null;
};

const cellStyleForStatus = (status) => {
  switch (status) {
    case 'To be rescheduled':
      return {
        backgroundColor: '#ffffff',
        color: '#cf1322',
        border: '1px solid #ff4d4f',
      };
    case 'Absent':
      return {
        backgroundColor: '#ff4d4f',
        color: '#ffffff',
        border: '1px solid #ff4d4f',
      };
    case 'Late':
      return {
        backgroundColor: '#fffbe6',
        color: '#ad6800',
        border: '1px solid #faad14',
      };
    case 'Present':
    default:
      return {
        backgroundColor: '#678d5b',
        color: '#fff',
        border: '1px solid #678d5b',
      };
  }
};

// Drop the trailing "(Y.8/G.8)" qualifier when the block is too short to show
// subject and teacher on separate lines.
const compactSubject = (subject) => (subject || '').replace(/\s*\([^)]*\)\s*$/, '').trim();

// A class can carry several teachers; only the first fits a shared line.
const compactTeachers = (names) => {
  const parts = (names || '').split(/,\s*/).filter(Boolean);
  if (parts.length === 0) return '-';
  return parts.length > 1 ? `${parts[0]} +${parts.length - 1}` : parts[0];
};

// Spelled-out status when the block has a line to spare, a glyph when it does not.
const statusLabel = (status, lines) => {
  if (!status) return '';
  if (lines >= 3) return status === 'To be rescheduled' ? 'TBR' : status;
  return (
    { 'To be rescheduled': 'TBR', Absent: '\u2715', Late: 'LATE', Present: '\u2713' }[status] || ''
  );
};

const ELLIPSIS = {
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const CourseSchedulePdfModal = ({
  open,
  onClose,
  course,
  classes,
  attendanceByClass = {},
  formatDuration,
}) => {
  const monthlyClasses = groupClassesByMonth(classes);

  const handleDownloadPDF = async () => {
    const contentElement = document.getElementById('pdf-preview-content');
    if (!contentElement) {
      message.error('Content not found');
      return;
    }

    const hideLoading = message.loading('Generating PDF...', 0);

    const style = document.createElement('style');
    style.id = 'pdf-export-disable-animations';
    style.textContent = `
      #pdf-preview-content,
      #pdf-preview-content *,
      #pdf-preview-content *::before,
      #pdf-preview-content *::after {
        transition: none !important;
        animation: none !important;
        opacity: 1 !important;
      }
      .month-page {
        page-break-after: always;
        page-break-inside: avoid;
      }
    `;
    document.head.appendChild(style);

    void contentElement.offsetHeight;
    // Wait for webfonts and one paint rather than sleeping for a fixed 500ms
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }
    await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 50)));

    try {
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;

      const monthContainers = document.querySelectorAll('.month-page');

      for (let i = 0; i < monthContainers.length; i++) {
        const monthElement = monthContainers[i];

        const canvas = await html2canvas(monthElement, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
        });

        // JPEG, not PNG: jsPDF embeds JPEG bytes as-is but has to parse and
        // re-deflate PNG in JS, which for a full-page calendar costs seconds per
        // page and produced ~11MB of PDF per page.
        const imgData = canvas.toDataURL('image/jpeg', 0.95);

        // The canvas already knows its dimensions, so there is no need to make
        // jsPDF parse the image again just to read them back.
        const imgRatio = canvas.height / canvas.width;

        if (i > 0) pdf.addPage();

        if (i === 0) {
          try {
            const logoImg = new Image();
            logoImg.crossOrigin = 'anonymous';
            await new Promise((resolve, reject) => {
              logoImg.onload = resolve;
              logoImg.onerror = reject;
              logoImg.src = '/images/keaes.png';
            });

            const logoCanvas = document.createElement('canvas');
            const logoCtx = logoCanvas.getContext('2d');
            logoCanvas.width = logoImg.width;
            logoCanvas.height = logoImg.height;
            logoCtx.drawImage(logoImg, 0, 0);
            const logoDataUrl = logoCanvas.toDataURL('image/png');

            pdf.setFillColor(248, 249, 250);
            pdf.rect(0, 0, pageWidth, 25, 'F');

            const logoHeight = 15;
            const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
            pdf.addImage(logoDataUrl, 'PNG', margin, 5, logoWidth, logoHeight);

            pdf.setFont('Helvetica', 'bold');
            pdf.setFontSize(14);
            pdf.setTextColor(33, 37, 41);
            pdf.text(`${course.class_code}`, margin + logoWidth + 10, 12);

            pdf.setFont('Helvetica', 'normal');
            pdf.setFontSize(9);
            pdf.setTextColor(108, 117, 125);
            const dateText = `Generated: ${moment().format('MMMM D, YYYY')} ${moment().format('hh:mm A')}`;
            const dateWidth = pdf.getTextWidth(dateText);
            pdf.text(dateText, pageWidth - margin - dateWidth, 12);
          } catch (logoError) {
            console.warn('Could not load logo:', logoError);
          }

          const availableHeight = pageHeight - 30 - margin;
          const imgWidth = pageWidth - margin * 2;
          const imgHeight = Math.min(imgWidth * imgRatio, availableHeight);
          pdf.addImage(imgData, 'JPEG', margin, 28, imgWidth, imgHeight);
        } else {
          const imgWidth = pageWidth - margin * 2;
          const availableHeight = pageHeight - margin * 2;
          const imgHeight = Math.min(imgWidth * imgRatio, availableHeight);
          pdf.addImage(imgData, 'JPEG', margin, margin, imgWidth, imgHeight);
        }
      }

      pdf.setFont('Helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(108, 117, 125);
      const footerText =
        'This schedule is generated automatically and subject to updates. © KEAES ACADEMICS';
      const footerWidth = pdf.getTextWidth(footerText);
      pdf.text(footerText, (pageWidth - footerWidth) / 2, pageHeight - 5);

      pdf.save(`${course.class_code}-schedule-${moment().format('YYYY-MM-DD')}.pdf`);
      message.success('PDF downloaded successfully');
      onClose();
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to generate PDF');
    } finally {
      hideLoading();
      const tempStyle = document.getElementById('pdf-export-disable-animations');
      if (tempStyle) tempStyle.remove();
    }
  };

  const totalDuration = classes.reduce(
    (sum, cls) => sum + (parseInt(cls.schedule_hour, 10) || 0),
    0
  );

  // Every month renders into an identically sized box whose aspect ratio matches the
  // area jsPDF has available on an A4 landscape page (267mm x 165mm => 0.618).
  // Row height is then derived from the box, so a 5-week and a 6-week month both
  // fill the page exactly instead of collapsing to their content height.
  const PAGE_W = 1100;
  const PAGE_H = 680;
  const TITLE_H = 44;
  const TITLE_GAP = 10;
  const HEADER_H = 30;
  const LEGEND_H = 20;
  const LEGEND_GAP = 6;
  // Slack so collapsed borders and line-height rounding can't add up to more than
  // the box and push the legend out of view.
  const SAFETY_H = 10;
  const GRID_BODY_H =
    PAGE_H - TITLE_H - TITLE_GAP - HEADER_H - LEGEND_H - LEGEND_GAP - SAFETY_H;

  // Space a day cell has for event blocks, once the td padding and the day
  // number are taken out.
  const CELL_PADDING = 4;
  const DAY_NUMBER_H = 18; // 16px line + 2px margin
  const cellContentHeight = (rowHeight) => rowHeight - DAY_NUMBER_H - CELL_PADDING * 2;

  // Weeks are not equally busy, so hand a week that stacks three classes on one
  // day more of the page than a week with one or none. Splitting GRID_BODY_H
  // evenly is what forced a two-class day down to the minimum font size.
  const MIN_ROW_H = 58;
  const weekRowHeights = (calendar, monthClasses) => {
    const weights = calendar.map((week) => {
      const busiest = week.reduce(
        (max, day) => Math.max(max, getClassesForDate(day, monthClasses).length),
        0
      );
      return 1 + Math.min(busiest, 4) * 0.5;
    });
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    const heights = weights.map((w) =>
      Math.max(MIN_ROW_H, Math.floor((GRID_BODY_H * w) / totalWeight))
    );
    // Raising short rows to MIN_ROW_H can push the grid past the page, so take
    // the surplus back off the tallest rows, which have height to spare.
    let surplus = heights.reduce((a, b) => a + b, 0) - GRID_BODY_H;
    while (surplus > 0) {
      let tallest = 0;
      for (let i = 1; i < heights.length; i++) {
        if (heights[i] > heights[tallest]) tallest = i;
      }
      if (heights[tallest] <= MIN_ROW_H) break;
      heights[tallest] -= 1;
      surplus -= 1;
    }
    return heights;
  };

  // How a single day's blocks are laid out: three lines (time+status, subject,
  // teacher) while that stays legible, otherwise two, with the teacher folded
  // onto the subject line. Every line is truncated rather than wrapped, so the
  // budget below is exactly what the block costs.
  const EVENT_GAP = 2;
  const EVENT_CHROME_H = 6; // 2px padding + 1px border, top and bottom
  const EVENT_LINE_RATIO = 1.22;
  const MIN_FONT = 6.5;
  const MAX_FONT = 11;
  const READABLE_FONT = 8;
  const planDay = (rowHeight, eventCount) => {
    const count = Math.max(1, eventCount);
    const available = cellContentHeight(rowHeight);
    const perEvent = (available - (count - 1) * EVENT_GAP) / count;
    const fontFor = (lines) => (perEvent - EVENT_CHROME_H) / (lines * EVENT_LINE_RATIO);
    const three = fontFor(3);
    if (three >= READABLE_FONT) return { lines: 3, font: Math.min(MAX_FONT, three) };
    return { lines: 2, font: Math.max(MIN_FONT, Math.min(MAX_FONT, fontFor(2))) };
  };

  return (
    <Modal
      title={
        <Space>
          <CalendarOutlined />
          <Text strong>Course Schedule - Calendar View</Text>
        </Space>
      }
      open={open}
      onCancel={onClose}
      width="95%"
      style={{ top: 20 }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Close
        </Button>,
        <Button key="download" type="primary" icon={<FilePdfOutlined />} onClick={handleDownloadPDF}>
          Download PDF
        </Button>,
      ]}
    >
      <div
        id="pdf-preview-content"
        style={{ padding: '30px', backgroundColor: '#fff', minHeight: '800px' }}
      >
        <div
          style={{
            marginBottom: '25px',
            borderBottom: '2px solid #e8e8e8',
            paddingBottom: '15px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <Title level={2} style={{ margin: 0, marginBottom: '5px' }}>
              {course?.class_code}
            </Title>
          </div>
          <div style={{ textAlign: 'right' }}>
            <img
              src="/images/keaes.png"
              alt="Logo"
              style={{ height: '50px', marginBottom: '8px' }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div style={{ fontSize: '11px', color: '#666' }}>
              <div>Generated: {moment().format('MMMM D, YYYY')}</div>
              <div>Time: {moment().format('hh:mm A')}</div>
            </div>
          </div>
        </div>

        <div
          style={{
            marginBottom: '20px',
            padding: '12px 15px',
            backgroundColor: '#f5f5f5',
            borderRadius: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '12px',
          }}
        >
          <span>
            <strong>Total Classes:</strong> {classes.length}
          </span>
          <span>
            <strong>Total Hours:</strong> {formatDuration(totalDuration)}
          </span>
          <span>
            <strong>Period:</strong> {moment(course?.start_date).format('DD MMM')} -{' '}
            {moment(course?.end_date).format('DD MMM YYYY')}
          </span>
        </div>

        {Object.keys(monthlyClasses)
          .sort()
          .map((monthKey) => {
            const monthClasses = monthlyClasses[monthKey];
            const calendar = generateMonthCalendar(monthKey);
            const rowHeights = weekRowHeights(calendar, monthClasses);
            const gridHeight = rowHeights.reduce((a, b) => a + b, 0);

            return (
              <div
                key={monthKey}
                className="month-page"
                style={{
                  width: `${PAGE_W}px`,
                  height: `${PAGE_H}px`,
                  boxSizing: 'border-box',
                  margin: '0 auto 40px',
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  pageBreakAfter: 'always',
                }}
              >
                <Title
                  level={3}
                  style={{
                    margin: `0 0 ${TITLE_GAP}px 0`,
                    height: `${TITLE_H}px`,
                    flex: '0 0 auto',
                    lineHeight: `${TITLE_H}px`,
                    padding: '0 15px',
                    backgroundColor: '#678d5b',
                    color: '#fff',
                    borderRadius: '6px',
                    textAlign: 'center',
                    boxSizing: 'border-box',
                  }}
                >
                  {moment(monthKey, 'YYYY-MM').format('MMMM YYYY')}
                </Title>

                <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
                <table
                  style={{
                    width: '100%',
                    height: `${HEADER_H + gridHeight}px`,
                    borderCollapse: 'collapse',
                    tableLayout: 'fixed',
                  }}
                >
                  <thead>
                    <tr>
                      {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((day) => (
                        <th
                          key={day}
                          style={{
                            border: '1px solid #d9d9d9',
                            height: `${HEADER_H}px`,
                            padding: '0 8px',
                            backgroundColor: '#fafafa',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            textAlign: 'center',
                            width: '14.28%',
                            boxSizing: 'border-box',
                          }}
                        >
                          {day}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {calendar.map((week, weekIndex) => {
                      const weekRowHeight = rowHeights[weekIndex];
                      return (
                      <tr key={weekIndex} style={{ height: `${weekRowHeight}px` }}>
                        {week.map((day, dayIndex) => {
                          const dayClasses = getClassesForDate(day, monthClasses);
                          const plan = planDay(weekRowHeight, dayClasses.length);
                          const evFont = plan.font;
                          const evLine = evFont * EVENT_LINE_RATIO;
                          const isCurrentMonth = day.format('YYYY-MM') === monthKey;
                          const isToday =
                            day.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');

                          return (
                            <td
                              key={dayIndex}
                              style={{
                                border: '1px solid #d9d9d9',
                                padding: `${CELL_PADDING}px`,
                                verticalAlign: 'top',
                                height: `${weekRowHeight}px`,
                                boxSizing: 'border-box',
                                overflow: 'hidden',
                                backgroundColor: isToday
                                  ? '#e6f7ff'
                                  : isCurrentMonth
                                  ? '#fff'
                                  : '#fafafa',
                                opacity: isCurrentMonth ? 1 : 0.5,
                                position: 'relative',
                                width: '14.28%',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '14px',
                                  lineHeight: '16px',
                                  height: '16px',
                                  fontWeight: isToday ? 'bold' : 'normal',
                                  color: isToday ? '#1890ff' : '#000',
                                  marginBottom: '2px',
                                }}
                              >
                                {day.format('D')}
                              </div>

                              {/* Bounded so a busy day clips instead of stretching the row */}
                              <div
                                style={{
                                  maxHeight: `${cellContentHeight(weekRowHeight)}px`,
                                  overflow: 'hidden',
                                }}
                              >
                                {dayClasses.map((cls, idx) => {
                                  const startTime = moment(cls.schedule_time, 'HH:mm:ss').format(
                                    'HH:mm'
                                  );
                                  const endTime = moment(startTime, 'HH:mm')
                                    .add(cls.schedule_hour, 'minutes')
                                    .format('HH:mm');

                                  const platformIcon =
                                    {
                                      online: '💻',
                                      onsite: '📍',
                                      athome: '🏠',
                                    }[cls.platform] || '❓';

                                  const status = resolveClassStatus(attendanceByClass[cls.classid]);
                                  const statusStyle = cellStyleForStatus(status);

                                  return (
                                    <div
                                      key={idx}
                                      style={{
                                        fontSize: `${evFont}px`,
                                        lineHeight: `${evLine}px`,
                                        padding: '2px 4px',
                                        // No trailing gap on the last block, so the
                                        // height budget above matches reality exactly.
                                        marginBottom:
                                          idx === dayClasses.length - 1 ? 0 : `${EVENT_GAP}px`,
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                        boxSizing: 'border-box',
                                        ...statusStyle,
                                      }}
                                      title={`${startTime}-${endTime} ${cls.subject_name} — ${
                                        cls.teacher_names || '-'
                                      }${status ? ` (${status})` : ''}`}
                                    >
                                      {/* Time and status share a row to save a line */}
                                      <div
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          gap: '4px',
                                          fontWeight: 'bold',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        <span>
                                          {startTime}-{endTime}
                                        </span>
                                        {status && <span>{statusLabel(status, plan.lines)}</span>}
                                      </div>
                                      <div style={{ display: 'flex', gap: '4px', whiteSpace: 'nowrap' }}>
                                        <span style={ELLIPSIS}>
                                          {platformIcon}{' '}
                                          {plan.lines >= 3
                                            ? cls.subject_name
                                            : compactSubject(cls.subject_name)}
                                        </span>
                                        {plan.lines < 3 && (
                                          <span
                                            style={{
                                              flex: '0 1 auto',
                                              maxWidth: '50%',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                            }}
                                          >
                                            👤 {compactTeachers(cls.teacher_names)}
                                          </span>
                                        )}
                                      </div>
                                      {plan.lines >= 3 && (
                                        <div
                                          style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          👤 {cls.teacher_names || '-'}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>

                <div
                  style={{
                    fontSize: '10px',
                    color: '#666',
                    marginTop: `${LEGEND_GAP}px`,
                    height: `${LEGEND_H}px`,
                    flex: '0 0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    overflow: 'hidden',
                  }}
                >
                  <span>💻 Online</span>
                  <span>📍 Onsite</span>
                  <span>🏠 At Home</span>
                  <span>👤 Teacher</span>
                  <span style={{ marginLeft: 'auto' }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, background: '#678d5b', marginRight: 4, verticalAlign: 'middle' }} />
                    Present / scheduled
                  </span>
                  <span>
                    <span style={{ display: 'inline-block', width: 10, height: 10, background: '#ff4d4f', border: '2px solid #ff4d4f', marginRight: 4, verticalAlign: 'middle' }} />
                    Absent
                  </span>
                  <span>
                    <span style={{ display: 'inline-block', width: 10, height: 10, background: '#fff', border: '2px solid #ff4d4f', marginRight: 4, verticalAlign: 'middle' }} />
                    To be rescheduled
                  </span>
                </div>
              </div>
            );
          })}

        <div
          style={{
            marginTop: '20px',
            paddingTop: '15px',
            borderTop: '1px solid #e8e8e8',
            textAlign: 'center',
            fontSize: '10px',
            color: '#999',
          }}
        >
          This schedule is generated automatically and subject to updates. © KEAES ACADEMICS
        </div>
      </div>
    </Modal>
  );
};

export default CourseSchedulePdfModal;
